import { Chart } from 'chart.js/auto';

import DialogController from '../dialog-controller';

import.meta.stimulusFetch = 'eager';

export default class extends DialogController {
    static targets = ['dialog', 'actions', 'canvas'];

    connect() {
        this.average = 0;
        this.chart = null;
        this.initChart();
        document.addEventListener('grade-total-updated', this.updateNeedle);
    }

    disconnect() {
        document.removeEventListener('grade-total-updated', this.updateNeedle);
        if (this.chart) {
            this.chart.destroy();
        }
    }

    open() {
        super.open();
        this.updateChartVisuals();
    }

    get needlePlugin() {
        return {
            id: 'needle',
            afterDatasetsDraw: (chart, args, plugins) => {
                const { ctx } = chart;
                const firstArc = chart.getDatasetMeta(0).data[0];

                const maxValue = 20;
                const totalSum = chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                const startAngle = chart.options.rotation * (Math.PI / totalSum);
                const cursorAngle = (plugins.average / maxValue) * Math.PI;

                ctx.save();
                ctx.translate(firstArc.x, firstArc.y);
                ctx.rotate(startAngle + cursorAngle);
                ctx.beginPath();
                ctx.moveTo(0, -firstArc.outerRadius);
                ctx.lineTo(-2, 0);
                ctx.lineTo(2, 0);
                ctx.closePath();
                ctx.fillStyle = '#000000';
                ctx.fill();
                ctx.restore();
            },
        };
    }

    get dashedMarkerPlugin() {
        return {
            id: 'dashedMarker',
            afterDatasetsDraw(chart, args, plugins) {
                const { ctx } = chart;
                const firstArc = chart.getDatasetMeta(0).data[0];
                const { innerRadius, outerRadius } = firstArc;
                const doughnutWidth = outerRadius - innerRadius;

                const totalSum = chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                const backgroundColors = chart.data.datasets[0].backgroundColor;

                let startAngle = chart.options.rotation * (Math.PI / chart.data.datasets[0].circumference);

                chart.data.datasets[0].data.forEach((value, index) => {
                    const segmentAngle = (value / totalSum) * Math.PI;
                    const backgroundColor = backgroundColors[index + 1];
                    const markerOption = plugins.markers?.[index];

                    ctx.save();
                    ctx.translate(firstArc.x, firstArc.y);
                    ctx.rotate(startAngle + segmentAngle);
                    ctx.beginPath();

                    if (markerOption?.dashedLine ?? true) {
                        ctx.setLineDash([1]);
                        ctx.strokeStyle = backgroundColor;
                        ctx.lineWidth = 1;
                        ctx.moveTo(0, -outerRadius + doughnutWidth - 2);
                        ctx.lineTo(0, -outerRadius - 15);
                        ctx.stroke();
                    }

                    if (markerOption) {
                        ctx.font = '12px Atkinson Hyperlegible';
                        ctx.fillStyle = '#1C1818';
                        ctx.textAlign = 'center';
                        ctx.fillText(`${markerOption.average}`, 0, -outerRadius - 20);
                    }

                    ctx.restore();
                    startAngle += segmentAngle;
                });
            },
        };
    }

    initChart() {
        this.ctx = this.canvasTarget.getContext('2d');

        const chartData = {
            datasets: [
                {
                    backgroundColor: ['#BABCBE', '#0078AC', '#006A99', '#005073', '#009729', '#00651B'],
                    data: [10, 2, 2, 2, 2, 2],
                    borderWidth: 1,
                    circumference: 180,
                    spacing: 2,
                },
            ],
        };

        const chartOptions = {
            layout: {
                padding: {
                    top: 30,
                    right: 30,
                },
            },
            animation: false,
            responsive: true,
            aspectRatio: 1.5,
            rotation: -90,
            plugins: {
                tooltip: { enabled: false },
                legend: { display: false },
                needle: { average: this.average },
                dashedMarker: {
                    markers: [
                        { average: 10 },
                        { average: 12 },
                        { average: 14 },
                        { average: 16 },
                        { average: 18 },
                        { average: 20 },
                    ],
                },
            },
        };

        this.chart = new Chart(this.ctx, {
            type: 'doughnut',
            data: chartData,
            options: chartOptions,
            plugins: [this.needlePlugin, this.dashedMarkerPlugin],
        });
    }

    updateNeedle = (event) => {
        this.average = event.detail.total ?? 0;
        this.updateChartVisuals();
    };

    updateChartVisuals() {
        if (this.chart) {
            this.chart.options.plugins.needle.average = this.average;
            this.chart.update('none');
        }
    }
}
